import React from "react";

interface PageLoadingStateProps {
  showAnalytics?: boolean;
}

const PageLoadingState: React.FC<PageLoadingStateProps> = ({ showAnalytics = true }) => {
  return (
    <div className="h-full w-full bg-gray-50 overflow-hidden flex flex-col">
      <div className="flex-grow overflow-auto p-4 md:p-6 m-3 bg-background text-foreground border border-gray-100 rounded-xl">
        {/* Header Skeleton */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-6">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Analytics Toggle Skeleton */}
        <div className="mb-6">
          <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
        </div>

        <div className="space-y-6">
          {/* Analytics Section Skeleton */}
          {showAnalytics && (
            <>
              {/* Statistics Cards Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                  >
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>

              {/* Graphs Skeleton */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-64">
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4" />
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                        <div className="flex-1 h-8 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-64">
                  <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4" />
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="space-y-2">
                        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                        <div className="h-6 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Filters/Tabs Skeleton */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-9 w-24 bg-gray-200 rounded animate-pulse"
                />
              ))}
            </div>
            <div className="h-9 w-32 bg-gray-200 rounded animate-pulse" />
          </div>

          {/* Search Input Skeleton */}
          <div className="h-10 w-full bg-gray-200 rounded animate-pulse" />

          {/* Table Skeleton */}
          <div className="flex flex-col gap-4">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Table Header Skeleton */}
              <div className="bg-gray-50 border-b border-gray-200">
                <div className="flex gap-4 p-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="flex-1 h-4 bg-gray-200 rounded animate-pulse"
                    />
                  ))}
                </div>
              </div>

              {/* Table Rows Skeleton */}
              <div className="divide-y divide-gray-200">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                  <div key={i} className="flex gap-4 p-4">
                    {[1, 2, 3, 4, 5].map((j) => (
                      <div
                        key={j}
                        className="flex-1 h-4 bg-gray-100 rounded animate-pulse"
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Skeleton */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLoadingState;
